import { useMemo } from "react";
import { useAppSelector } from "../redux/hooks";
import ThemeLine from "../components/custom/merchandise/ThemeLine";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../zodios/client";

export const useCategoriesQuery = (marketplace?: string, channel?: string) => {
  const selectedMarketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const selectedChannel = useAppSelector((state) => state.channel.currentChannel);
  return useQuery({
    queryKey: [{ entity: "categories", type: "list", marketplace, channel }],
    queryFn: async () =>
      await apiClient.getCategories({
        queries: { marketplace: marketplace ?? selectedMarketPlace, channel: channel ?? selectedChannel },
      }),
    staleTime: 5 * 60 * 1000,
  });
};

const useCategories = (categoryId: string | undefined, dashboardRoute: string) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const categoryQuery = useCategoriesQuery(marketPlace, channel);

  const currentCategoryData = categoryQuery.data?.find((category) => category.id === parseInt(categoryId ?? ""));

  const currentCategoryDetails = useMemo(
    () => ({
      ...currentCategoryData,
      name: currentCategoryData?.leaf_category_name,
      href: `/user/${dashboardRoute}/category/${currentCategoryData?.id}`,
    }),
    [currentCategoryData, dashboardRoute],
  );

  // Memoized because callers feed this straight into `columns`/`items`.
  const categories = useMemo(
    () =>
      categoryQuery.data?.map((category) => {
        return {
          ...category,
          name: category.leaf_category_name,
          altDisplayMarkup: (
            <div>
              <span
                style={{
                  maxWidth: "100%",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontWeight: category.leaf_category_name === currentCategoryData?.leaf_category_name ? 600 : "inherit",
                  fontSize: "1.5rem",
                }}
              >
                {category.leaf_category_name}
              </span>
              <ThemeLine
                additionalStyles={{
                  fontSize: "10px",
                  fontWeight: category.leaf_category_name === currentCategoryData?.leaf_category_name ? 500 : "inherit",
                  color: "#2e0054",
                }}
              >
                {category.ad_count} Media Plan<span>{category.ad_count === 1 ? "" : "s"}</span>,{" "}
                {category.product_count} Product<span>{category.product_count === 1 ? "" : "s"}</span>
              </ThemeLine>
            </div>
          ),
          href: `/user/${dashboardRoute}/category/${category.id}`,
        };
      }),
    [categoryQuery.data, currentCategoryData, dashboardRoute],
  );

  return { currentCategoryDetails, categories };
};

export default useCategories;
